import { resolverNetoPorDiaBoletaConsu } from './periodoCalculoConsu';

/**
 * Plantilla HTML de boleta de pago (PDF vía html2canvas).
 * Estilos: "colorido" | "estandar" (casi incolora) | "azul_oro" (navy / cian).
 * Misma estructura de datos en todos; solo cambia la paleta.
 * El logo (si existe) solo aparece en boleta PLAME; nunca en boleta interna.
 */

function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function calcularRemuneracionBasicaBoleta(empleado, planilla, { usarInterna = false } = {}) {
  if (usarInterna) {
    const sueldoTramo = Number(
      empleado?.sueldoInternoTramo
        ?? empleado?.sueldo_interno_tramo
        ?? empleado?.sueldoInterno1
        ?? empleado?.sueldo_interno_1
    );
    if (!Number.isNaN(sueldoTramo) && sueldoTramo > 0 && (empleado?.sueldoInternoTramo != null || empleado?.sueldo_interno_tramo != null)) {
      return Math.trunc(sueldoTramo * 100) / 100;
    }

    const numInterno = Number(
      empleado?.sueldo_interno
        ?? empleado?.sueldoInterno
        ?? empleado?.datosEmpleado?.sueldo_interno
        ?? empleado?.datosSalariales?.sueldoInterno
    );
    if (!Number.isNaN(numInterno) && numInterno > 0) {
      return Math.trunc(numInterno * 100) / 100;
    }

    const conceptoBasicoInterno = (empleado?.conceptos?.ingresos || []).find((concepto) => {
      const codigo = String(concepto.codigo || concepto.codigoPlame || '').trim();
      if (codigo === '0121') return true;
      const desc = String(concepto.descripcion || '').toLowerCase();
      return desc.includes('remuneración') || desc.includes('remuneracion')
        ? (desc.includes('básico') || desc.includes('basico') || desc.includes('jornal'))
        : false;
    });
    if (conceptoBasicoInterno != null && Number(conceptoBasicoInterno.monto) > 0) {
      return Math.trunc(Number(conceptoBasicoInterno.monto) * 100) / 100;
    }
  }

  const numSueldo = Number(empleado?.sueldo_basico);
  const sueldoCerrado =
    empleado?.sueldo_basico != null &&
    empleado?.sueldo_basico !== '' &&
    !Number.isNaN(numSueldo) &&
    numSueldo > 0;

  if (sueldoCerrado) {
    return Math.trunc(numSueldo * 100) / 100;
  }

  const conceptoBasico = empleado?.conceptos?.ingresos?.find(
    (concepto) =>
      concepto.descripcion?.toLowerCase().includes('remuneración') &&
      (concepto.descripcion?.toLowerCase().includes('básico') ||
        concepto.descripcion?.toLowerCase().includes('jornal'))
  );

  const monto =
    conceptoBasico?.monto ??
    empleado?.datosSalariales?.sueldoBasico ??
    empleado?.sueldo_basico ??
    0;
  const totalTareos = empleado?.resumenTareos?.total_tareos ?? 0;
  const fechaPlanilla = planilla?.fecha_inicio || planilla?.fecha_fin;
  const mesPlanilla = fechaPlanilla
    ? (fechaPlanilla.toString().split('T')[0].split('-')[1] || '').replace(/^0+/, '') || null
    : null;
  const esFebrero = mesPlanilla === '2' || mesPlanilla === 2;
  const divisor =
    esFebrero && (totalTareos === 28 || totalTareos === 29)
      ? 30
      : totalTareos > 30
        ? 30
        : totalTareos || 1;
  const resultado = totalTareos <= 0 ? monto : (Number(monto) / divisor) * 30;
  return Math.trunc(Number(resultado) * 100) / 100;
}

function normalizarEstiloBoleta(estiloBoleta, boletaEstandar) {
  const estilo = String(estiloBoleta || '').toLowerCase().trim();
  if (estilo === 'estandar' || estilo === 'azul_oro' || estilo === 'colorido') {
    return estilo;
  }
  return boletaEstandar ? 'estandar' : 'colorido';
}

function temasBoleta(estiloBoleta) {
  // Estándar: casi incoloro; solo franjas laterales de color en las 3 tarjetas de datos.
  if (estiloBoleta === 'estandar') {
    return {
      contenedor:
        'padding: 25px; background: white; margin-bottom: 15px; border: 2px solid #d1d5db; border-radius: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.06);',
      tituloEmpresa: 'color: #111827; margin-bottom: 8px; font-size: 24px; font-weight: bold; letter-spacing: 0.5px;',
      tituloBoleta: 'color: #111827; font-size: 18px; margin-top: 10px; font-weight: bold; letter-spacing: 0.5px;',
      textoMuted: 'color: #6b7280; font-size: 14px; margin: 5px 0;',
      headerBorder: 'border-bottom: 2px solid #e5e7eb;',
      card: 'background-color: #ffffff; padding: 15px; border-radius: 8px; border: 1px solid #e5e7eb;',
      cardTitulo:
        'color: #111827; font-size: 14px; font-weight: bold; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #e5e7eb;',
      cardAccentPersonal: 'border-left: 4px solid #2563eb;',
      cardAccentLaboral: 'border-left: 4px solid #6366f1;',
      cardAccentAportes: 'border-left: 4px solid #0ea5e9;',
      seccionBordeIngresos: 'border: 1px solid #d1d5db; border-radius: 8px; overflow: hidden;',
      seccionBordeDescuentos: 'border: 1px solid #d1d5db; border-radius: 8px; overflow: hidden;',
      seccionBordeAportes: 'border: 1px solid #d1d5db; border-radius: 8px; overflow: hidden;',
      seccionHeaderIngresos:
        'color: #111827; font-size: 13px; font-weight: bold; margin: 0; background-color: #f3f4f6; padding: 10px; text-align: center;',
      seccionHeaderDescuentos:
        'color: #111827; font-size: 13px; font-weight: bold; margin: 0; background-color: #f3f4f6; padding: 10px; text-align: center;',
      seccionHeaderAportes:
        'color: #111827; font-size: 13px; font-weight: bold; margin: 0; background-color: #f3f4f6; padding: 10px; text-align: center;',
      seccionBodyIngresos: 'min-height: 120px; margin-bottom: 0; padding: 10px; background-color: #ffffff;',
      seccionBodyDescuentos: 'min-height: 120px; margin-bottom: 0; padding: 10px; background-color: #ffffff;',
      seccionBodyAportes: 'min-height: 120px; margin-bottom: 0; padding: 10px; background-color: #ffffff;',
      filaBordeIngresos: 'border-bottom: 1px solid #e5e7eb;',
      filaBordeDescuentos: 'border-bottom: 1px solid #e5e7eb;',
      filaBordeAportes: 'border-bottom: 1px solid #e5e7eb;',
      montoIngresos: 'color: #111827; font-weight: bold;',
      montoDescuentos: 'color: #111827; font-weight: bold;',
      montoAportes: 'color: #111827; font-weight: bold;',
      totalBoxIngresos: 'padding: 10px; background-color: #f3f4f6; text-align: center; border-top: 1px solid #d1d5db;',
      totalBoxDescuentos: 'padding: 10px; background-color: #f3f4f6; text-align: center; border-top: 1px solid #d1d5db;',
      totalBoxAportes: 'padding: 10px; background-color: #f3f4f6; text-align: center; border-top: 1px solid #d1d5db;',
      totalTextoIngresos: 'font-weight: bold; color: #111827; font-size: 12px;',
      totalTextoDescuentos: 'font-weight: bold; color: #111827; font-size: 12px;',
      totalTextoAportes: 'font-weight: bold; color: #111827; font-size: 12px;',
      neto:
        'background: #374151; color: white; padding: 18px; text-align: center; border-radius: 8px; margin-bottom: 20px; box-shadow: none;',
      firmaBox:
        'text-align: center; background-color: #ffffff; padding: 20px; border-radius: 10px; border: 1px solid #e5e7eb;',
    };
  }

  // Azul oro: misma estructura que estándar; paleta navy / cian (sin dorado).
  if (estiloBoleta === 'azul_oro') {
    const navy = '#001A72';
    const cyan = '#3EB5E5';
    return {
      contenedor:
        `padding: 25px; background: white; margin-bottom: 15px; border: 2px solid ${navy}; border-radius: 12px; box-shadow: 0 2px 6px rgba(0,26,114,0.12);`,
      tituloEmpresa: `color: ${navy}; margin-bottom: 8px; font-size: 24px; font-weight: bold; letter-spacing: 0.5px;`,
      tituloBoleta: `color: ${navy}; font-size: 18px; margin-top: 10px; font-weight: bold; letter-spacing: 0.5px;`,
      textoMuted: 'color: #4b5563; font-size: 14px; margin: 5px 0;',
      headerBorder: `border-bottom: 3px solid ${cyan};`,
      card: 'background-color: #ffffff; padding: 15px; border-radius: 8px; border: 1px solid #dbeafe;',
      cardTitulo:
        `color: ${navy}; font-size: 14px; font-weight: bold; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid ${cyan};`,
      cardAccentPersonal: `border-left: 4px solid ${navy};`,
      cardAccentLaboral: `border-left: 4px solid ${cyan};`,
      cardAccentAportes: `border-left: 4px solid ${navy};`,
      seccionBordeIngresos: `border: 1px solid ${navy}; border-radius: 8px; overflow: hidden;`,
      seccionBordeDescuentos: `border: 1px solid ${navy}; border-radius: 8px; overflow: hidden;`,
      seccionBordeAportes: `border: 1px solid ${navy}; border-radius: 8px; overflow: hidden;`,
      seccionHeaderIngresos:
        `color: #ffffff; font-size: 13px; font-weight: bold; margin: 0; background-color: ${navy}; padding: 10px; text-align: center;`,
      seccionHeaderDescuentos:
        `color: #ffffff; font-size: 13px; font-weight: bold; margin: 0; background-color: ${navy}; padding: 10px; text-align: center;`,
      seccionHeaderAportes:
        `color: #ffffff; font-size: 13px; font-weight: bold; margin: 0; background-color: ${navy}; padding: 10px; text-align: center;`,
      seccionBodyIngresos: 'min-height: 120px; margin-bottom: 0; padding: 10px; background-color: #ffffff;',
      seccionBodyDescuentos: 'min-height: 120px; margin-bottom: 0; padding: 10px; background-color: #ffffff;',
      seccionBodyAportes: 'min-height: 120px; margin-bottom: 0; padding: 10px; background-color: #ffffff;',
      filaBordeIngresos: 'border-bottom: 1px solid #e0f2fe;',
      filaBordeDescuentos: 'border-bottom: 1px solid #e0f2fe;',
      filaBordeAportes: 'border-bottom: 1px solid #e0f2fe;',
      montoIngresos: `color: ${navy}; font-weight: bold;`,
      montoDescuentos: `color: ${navy}; font-weight: bold;`,
      montoAportes: `color: ${navy}; font-weight: bold;`,
      totalBoxIngresos: `padding: 10px; background-color: #e8f7fc; text-align: center; border-top: 1px solid ${cyan};`,
      totalBoxDescuentos: `padding: 10px; background-color: #e8f7fc; text-align: center; border-top: 1px solid ${cyan};`,
      totalBoxAportes: `padding: 10px; background-color: #e8f7fc; text-align: center; border-top: 1px solid ${cyan};`,
      totalTextoIngresos: `font-weight: bold; color: ${navy}; font-size: 12px;`,
      totalTextoDescuentos: `font-weight: bold; color: ${navy}; font-size: 12px;`,
      totalTextoAportes: `font-weight: bold; color: ${navy}; font-size: 12px;`,
      neto:
        `background: ${navy}; color: #ffffff; padding: 18px; text-align: center; border-radius: 8px; margin-bottom: 20px; border: 2px solid ${navy}; box-shadow: none;`,
      firmaBox:
        `text-align: center; background-color: #ffffff; padding: 20px; border-radius: 10px; border: 1px solid ${cyan};`,
    };
  }

  return {
    contenedor:
      'padding: 25px; background: white; margin-bottom: 15px; border: 3px solid #1f2937; border-radius: 12px; box-shadow: 0 4px 8px rgba(0,0,0,0.15);',
    tituloEmpresa: 'color: #1f2937; margin-bottom: 8px; font-size: 24px; font-weight: bold; letter-spacing: 1px;',
    tituloBoleta: 'color: #059669; font-size: 18px; margin-top: 10px; font-weight: bold; letter-spacing: 0.5px;',
    textoMuted: 'color: #6b7280; font-size: 14px; margin: 5px 0;',
    headerBorder: 'border-bottom: 2px solid #e5e7eb;',
    card: 'background-color: #f9fafb; padding: 15px; border-radius: 8px;',
    cardTitulo:
      'color: #374151; font-size: 14px; font-weight: bold; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #d1d5db;',
    cardAccentPersonal: 'border-left: 4px solid #3b82f6;',
    cardAccentLaboral: 'border-left: 4px solid #10b981;',
    cardAccentAportes: 'border-left: 4px solid #f59e0b;',
    seccionBordeIngresos: 'border: 2px solid #065f46; border-radius: 10px; overflow: hidden;',
    seccionBordeDescuentos: 'border: 2px solid #dc2626; border-radius: 10px; overflow: hidden;',
    seccionBordeAportes: 'border: 2px solid #1e40af; border-radius: 10px; overflow: hidden;',
    seccionHeaderIngresos:
      'color: #ffffff; font-size: 14px; font-weight: bold; margin: 0; background-color: #065f46; padding: 12px; text-align: center;',
    seccionHeaderDescuentos:
      'color: #ffffff; font-size: 14px; font-weight: bold; margin: 0; background-color: #dc2626; padding: 12px; text-align: center;',
    seccionHeaderAportes:
      'color: #ffffff; font-size: 14px; font-weight: bold; margin: 0; background-color: #1e40af; padding: 12px; text-align: center;',
    seccionBodyIngresos: 'min-height: 120px; margin-bottom: 0; padding: 10px; background-color: #f0fdf4;',
    seccionBodyDescuentos: 'min-height: 120px; margin-bottom: 0; padding: 10px; background-color: #fef2f2;',
    seccionBodyAportes: 'min-height: 120px; margin-bottom: 0; padding: 10px; background-color: #eff6ff;',
    filaBordeIngresos: 'border-bottom: 1px solid #bbf7d0;',
    filaBordeDescuentos: 'border-bottom: 1px solid #fca5a5;',
    filaBordeAportes: 'border-bottom: 1px solid #93c5fd;',
    montoIngresos: 'color: #065f46; font-weight: bold;',
    montoDescuentos: 'color: #dc2626; font-weight: bold;',
    montoAportes: 'color: #1e40af; font-weight: bold;',
    totalBoxIngresos: 'padding: 10px; background-color: #dcfce7; text-align: center; border-top: 1px solid #16a34a;',
    totalBoxDescuentos: 'padding: 10px; background-color: #fee2e2; text-align: center; border-top: 1px solid #ef4444;',
    totalBoxAportes: 'padding: 10px; background-color: #dbeafe; text-align: center; border-top: 1px solid #3b82f6;',
    totalTextoIngresos: 'font-weight: bold; color: #065f46; font-size: 12px;',
    totalTextoDescuentos: 'font-weight: bold; color: #dc2626; font-size: 12px;',
    totalTextoAportes: 'font-weight: bold; color: #1e40af; font-size: 12px;',
    neto:
      'background: linear-gradient(135deg, #0f766e, #14b8a6); color: white; padding: 20px; text-align: center; border-radius: 12px; margin-bottom: 20px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);',
    firmaBox:
      'text-align: center; background-color: #f9fafb; padding: 20px; border-radius: 10px; border: 2px solid #e5e7eb;',
  };
}

function filasConceptosIngresos(empleado, formatCurrency, tema) {
  const conceptos = empleado?.conceptos?.ingresos || [];
  const conceptoBasico = conceptos.find(
    (concepto) =>
      concepto.descripcion?.toLowerCase().includes('remuneración') &&
      (concepto.descripcion?.toLowerCase().includes('básico') ||
        concepto.descripcion?.toLowerCase().includes('jornal'))
  );
  const otros = conceptos.filter((c) => c !== conceptoBasico);
  const ordenados = (conceptoBasico ? [conceptoBasico, ...otros] : conceptos)
    .filter((concepto) => Number(concepto.monto) > 0);

  return ordenados
    .map(
      (concepto) => `
      <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 11px; ${tema.filaBordeIngresos}">
        <span style="color: #374151; font-weight: 500;">
          ${escapeHtml(concepto.descripcion || concepto.nombre || concepto.concepto || concepto.codigo || 'INGRESO')}
          ${concepto.detalleSueldoTramo
            ? `<br/><span style="color: #92400e; font-size: 10px; font-weight: 600;">${escapeHtml(concepto.detalleSueldoTramo)}</span>`
            : ''}
        </span>
        <span style="${tema.montoIngresos}">${formatCurrency(concepto.monto)}</span>
      </div>`
    )
    .join('');
}

/**
 * @param {object} opts
 * @param {object} opts.empleado
 * @param {object} opts.empresa - { name, ruc }
 * @param {object} opts.planilla
 * @param {number} opts.numeroBoleta
 * @param {number} opts.totalBoletas
 * @param {boolean} [opts.boletaEstandar] - compat; preferir estiloBoleta
 * @param {string} [opts.estiloBoleta] - colorido | estandar | azul_oro
 * @param {string|null} opts.logoBase64
 * @param {string|null} opts.firmaBase64
 * @param {(n:number)=>string} opts.formatCurrency
 * @param {(d:string)=>string} opts.formatDate
 * @param {boolean} [opts.esSoloInterna] - boleta interna sin empresa/sistema/aportes empleador
 */
export function generarHtmlBoletaPago({
  empleado,
  empresa,
  planilla,
  numeroBoleta = 1,
  totalBoletas = 1,
  boletaEstandar = false,
  estiloBoleta = null,
  logoBase64 = null,
  firmaBase64 = null,
  formatCurrency,
  formatDate,
  esSoloInterna = false,
  aclaracionSueldoPartido = '',
  subtituloBoleta = '',
}) {
  const estilo = normalizarEstiloBoleta(estiloBoleta, boletaEstandar);
  const tema = temasBoleta(estilo);
  const soloInterna = Boolean(esSoloInterna);
  const nombreEmpresa = escapeHtml(empresa?.name || empresa?.nombre_empresa || 'EMPRESA');
  const ruc = escapeHtml(empresa?.ruc || 'N/A');
  const remuBasica = calcularRemuneracionBasicaBoleta(empleado, planilla, {
    usarInterna: soloInterna,
  });
  const periodoInicio = planilla?.fecha_inicio ? formatDate(planilla.fecha_inicio) : 'N/A';
  const periodoFin = planilla?.fecha_fin ? formatDate(planilla.fecha_fin) : 'N/A';
  const netoDiaConsu = resolverNetoPorDiaBoletaConsu(empleado, planilla);
  const textoPeriodoCalculo = netoDiaConsu?.override && netoDiaConsu.fechaInicio && netoDiaConsu.fechaFin
    ? `${formatDate(netoDiaConsu.fechaInicio)} – ${formatDate(netoDiaConsu.fechaFin)}`
    : '';
  const textoNetoPorDia = netoDiaConsu?.diasNeto
    ? `NETO POR DÍA: ${formatCurrency(netoDiaConsu.netoPorDia)} · ${netoDiaConsu.diasNeto} día${Number(netoDiaConsu.diasNeto) === 1 ? '' : 's'} de asistencia${textoPeriodoCalculo ? ` · ${textoPeriodoCalculo}` : ''}`
    : (Number(empleado.totales?.diasNeto) > 0 && empleado.totales?.netoPorDia != null
      ? `NETO POR DÍA: ${formatCurrency(empleado.totales.netoPorDia)} · ${empleado.totales.diasNeto} día${Number(empleado.totales.diasNeto) === 1 ? '' : 's'} de asistencia`
      : '');

  // Logo solo en PLAME (nunca en boleta interna): más grande y centrado en el hueco izquierdo
  const logoHtml =
    !soloInterna && logoBase64
      ? `<img src="${logoBase64}" alt="Logo" style="max-height: 118px; max-width: 175px; width: auto; height: auto; object-fit: contain; display: block;" />`
      : '';

  const cardPersonal = `${tema.card} ${tema.cardAccentPersonal}`;
  const cardLaboral = `${tema.card} ${tema.cardAccentLaboral}`;
  const cardAportesInfo = `${tema.card} ${tema.cardAccentAportes}`;

  const descuentos = empleado?.conceptos?.descuentos || [];
  const aportes = soloInterna ? [] : (empleado?.conceptos?.aportes || []);
  const tituloBoleta = 'BOLETA DE PAGO';
  const tituloCardAsistencia = soloInterna ? 'ASISTENCIA' : 'APORTES';
  const gridConceptos = soloInterna
    ? 'display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 25px;'
    : 'display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin-bottom: 25px;';

  const subtituloHtml = subtituloBoleta
    ? `<p style="color: #065f46; font-size: 13px; margin-top: 6px; font-weight: 700; letter-spacing: 0.4px;">${escapeHtml(subtituloBoleta)}</p>`
    : '';
  const aclaracionHtml = aclaracionSueldoPartido
    ? `<p style="color: #92400e; font-size: 11px; margin-top: 8px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 6px 8px;">${escapeHtml(aclaracionSueldoPartido)}</p>`
    : '';

  const encabezadoEmpresaHtml = soloInterna
    ? `
        <h2 style="${tema.tituloBoleta}">${tituloBoleta}</h2>
        ${subtituloHtml}
        <p style="color: #374151; font-size: 12px; margin-top: 6px; font-weight: 600;">Boleta N° ${numeroBoleta} de ${totalBoletas}</p>
        <p style="${tema.textoMuted}">Período: ${periodoInicio} - ${periodoFin}</p>
        ${textoPeriodoCalculo ? `<p style="color: #0369a1; font-size: 11px; margin-top: 4px; font-weight: 700;">Cálculo: ${textoPeriodoCalculo}</p>` : ''}
        ${aclaracionHtml}
      `
    : `
        <h1 style="${tema.tituloEmpresa}">${nombreEmpresa}</h1>
        <p style="${tema.textoMuted}">RUC: ${ruc}</p>
        <h2 style="${tema.tituloBoleta}">${tituloBoleta}</h2>
        ${subtituloHtml}
        <p style="color: #374151; font-size: 12px; margin-top: 6px; font-weight: 600;">Boleta N° ${numeroBoleta} de ${totalBoletas}</p>
        <p style="${tema.textoMuted}">Período: ${periodoInicio} - ${periodoFin}</p>
        ${textoPeriodoCalculo ? `<p style="color: #0369a1; font-size: 11px; margin-top: 4px; font-weight: 700;">Cálculo: ${textoPeriodoCalculo}</p>` : ''}
        ${aclaracionHtml}
      `;

  const sistemaPensionHtml = soloInterna
    ? ''
    : `
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Sistema:</strong> ${escapeHtml(empleado.tipoAporte || '')}</p>
          ${empleado.tipoAporte === 'AFP' ? `<p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>AFP:</strong> ${escapeHtml(empleado.afp || 'Sin AFP')}</p>` : ''}
        `;

  const seccionAportesEmpleadorHtml = soloInterna
    ? ''
    : `
        <div style="${tema.seccionBordeAportes}">
          <h3 style="${tema.seccionHeaderAportes}">APORTES EMPLEADOR</h3>
          <div style="${tema.seccionBodyAportes}">
            ${aportes
              .filter((concepto) => Number(concepto.monto) > 0)
              .map(
                (concepto) => `
              <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 11px; ${tema.filaBordeAportes}">
                <span style="color: #374151; font-weight: 500;">${escapeHtml(concepto.descripcion || concepto.nombre || concepto.concepto || concepto.codigo || 'APORTE')}</span>
                <span style="${tema.montoAportes}">${formatCurrency(concepto.monto)}</span>
              </div>`
              )
              .join('')}
          </div>
          <div style="${tema.totalBoxAportes}">
            <div style="${tema.totalTextoAportes}">TOTAL APORTES:<br/>${formatCurrency(empleado.totales?.totalAportes || 0)}</div>
          </div>
        </div>
      `;

  const pieEmpresaHtml = soloInterna
    ? `<p style="margin: 3px 0;">Sistema de Planillas — Boleta Interna</p>`
    : `<p style="margin: 3px 0;">Sistema de Planillas - ${nombreEmpresa}</p>`;

  return `
    <div style="${tema.contenedor}">
      <div style="position: relative; text-align: center; margin-bottom: 20px; padding-bottom: 15px; ${tema.headerBorder || 'border-bottom: 2px solid #e5e7eb;'} ${logoHtml ? 'min-height: 130px;' : ''}">
        ${
          logoHtml
            ? `<div style="position: absolute; left: 0; top: 50%; transform: translateY(-50%); width: 180px; text-align: left; display: flex; align-items: center; justify-content: flex-start;">${logoHtml}</div>`
            : ''
        }
        ${encabezadoEmpresaHtml}
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-bottom: 25px;">
        <div style="${cardPersonal}">
          <h3 style="${tema.cardTitulo}">DATOS PERSONALES</h3>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Nombre:</strong> ${escapeHtml(empleado.nombre)}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>DNI:</strong> ${escapeHtml(empleado.documento)}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Email:</strong> ${escapeHtml(empleado.email || 'No disponible')}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Teléfono:</strong> ${escapeHtml(empleado.telefono || 'No disponible')}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Dirección:</strong> ${escapeHtml(empleado.direccion || 'No disponible')}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Desc. Descanso:</strong> ${empleado.descuento_descanso ? 'Sí' : 'No'}</p>
        </div>
        <div style="${cardLaboral}">
          <h3 style="${tema.cardTitulo}">INFORMACIÓN LABORAL</h3>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Cargo:</strong> ${escapeHtml(empleado.cargo)}</p>
          ${
            soloInterna
              ? ''
              : `<p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Área:</strong> ${escapeHtml(empleado.areas_de_trabajo || 'Sin área')}</p>`
          }
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Proyecto:</strong> ${escapeHtml(empleado.proyecto || 'Sin proyecto')}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Categoría:</strong> ${escapeHtml(empleado.categoria)}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Régimen:</strong> ${escapeHtml(empleado.regimen_laboral_nombre || empleado.regimenLaboral || '')}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Fecha Ingreso:</strong> ${empleado.fechaIngreso ? formatDate(empleado.fechaIngreso) : 'No disponible'}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Remuneración Básica:</strong> ${formatCurrency(remuBasica)}</p>
        </div>
        <div style="${cardAportesInfo}">
          <h3 style="${tema.cardTitulo}">${tituloCardAsistencia}</h3>
          ${sistemaPensionHtml}
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Días Trabajados:</strong> ${empleado.resumenTareos?.total_tareos || 0}</p>
          <p style="margin: 4px 0; font-size: 11px; line-height: 1.4;"><strong>Horas Ordinarias:</strong> ${empleado.tareos?.horasOrdinarias || 0}</p>
        </div>
      </div>

      <div style="${gridConceptos}">
        <div style="${tema.seccionBordeIngresos}">
          <h3 style="${tema.seccionHeaderIngresos}">INGRESOS</h3>
          <div style="${tema.seccionBodyIngresos}">
            ${filasConceptosIngresos(empleado, formatCurrency, tema)}
          </div>
          <div style="${tema.totalBoxIngresos}">
            <div style="${tema.totalTextoIngresos}">TOTAL INGRESOS:<br/>${formatCurrency(empleado.totales?.totalIngresos || 0)}</div>
          </div>
        </div>

        <div style="${tema.seccionBordeDescuentos}">
          <h3 style="${tema.seccionHeaderDescuentos}">DESCUENTOS</h3>
          <div style="${tema.seccionBodyDescuentos}">
            ${descuentos
              .filter((concepto) => Number(concepto.monto) > 0)
              .map(
                (concepto) => `
              <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 11px; ${tema.filaBordeDescuentos}">
                <span style="color: #374151; font-weight: 500;">${escapeHtml(concepto.descripcion || concepto.nombre || concepto.concepto || concepto.codigo || 'DESCUENTO')}</span>
                <span style="${tema.montoDescuentos}">${formatCurrency(concepto.monto)}</span>
              </div>`
              )
              .join('')}
          </div>
          <div style="${tema.totalBoxDescuentos}">
            <div style="${tema.totalTextoDescuentos}">TOTAL DESCUENTOS:<br/>${formatCurrency(empleado.totales?.totalDescuentos || 0)}</div>
          </div>
        </div>

        ${seccionAportesEmpleadorHtml}
      </div>

      <div style="${tema.neto}">
        <h2 style="margin: 0; font-size: 20px; font-weight: bold; letter-spacing: 1px;">NETO A PAGAR: ${formatCurrency(empleado.totales?.netoAPagar || 0)}</h2>
        ${textoNetoPorDia
          ? `<p style="margin: 8px 0 0; font-size: 13px; font-weight: 600; letter-spacing: 0.3px; opacity: 0.95;">${textoNetoPorDia}</p>`
          : ''}
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 25px; margin-bottom: 20px;">
        <div style="${tema.firmaBox}">
          <div style="height: 60px; margin-bottom: 15px; position: relative; display: flex; align-items: center; justify-content: center;">
            ${
              firmaBase64
                ? `<img src="${firmaBase64}" style="width: 180px; height: auto; object-fit: contain; margin-top: 70px;" alt="Firma del Empleador" />`
                : `<p style="position: absolute; top: -10px; left: 50%; transform: translateX(-50%); background: #fff; padding: 0 10px; font-size: 10px; color: #6b7280;">Espacio para firma</p>`
            }
          </div>
        </div>
        <div style="${tema.firmaBox}">
          <div style="height: 60px; margin-bottom: 15px; border-bottom: 2px solid #374151; position: relative;">
            <p style="position: absolute; top: -10px; left: 50%; transform: translateX(-50%); background: #fff; padding: 0 10px; font-size: 10px; color: #6b7280;">Espacio para firma</p>
          </div>
          <div style="padding-top: 10px;">
            <p style="margin: 0; font-size: 12px; font-weight: bold; color: #374151;">FIRMA DEL TRABAJADOR</p>
            <p style="margin: 5px 0; font-size: 11px; color: #6b7280;">${escapeHtml(empleado.nombre)}</p>
            <p style="margin: 0; font-size: 10px; color: #6b7280;">DNI: ${escapeHtml(empleado.documento)}</p>
          </div>
        </div>
      </div>

      <div style="text-align: center; color: #6b7280; font-size: 10px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
        <p style="margin: 3px 0;">Fecha de generación: ${new Date().toLocaleDateString('es-PE')}</p>
        ${pieEmpresaHtml}
      </div>
    </div>
  `;
}
